// src/components/EditCompositeModal.js
//
// Opens as a full-screen overlay when the user wants to edit a private
// composite request and resubmit it for admin review.
//
// Data flow:
//   1. Mount  → fetch lookup tables for the item's region
//   2. Reconstruct env rows by grouping LineItemsData by description
//   3. User tweaks quantities / adds / removes rows
//   4. Submit → POST /composite-requests  (new pending request, same flow)

import React, {
  useState, useEffect, useMemo, useCallback, useContext,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { resolveLineItemLinks, normKey } from '../utils/quantityLinks';
import { typeMatchesBase } from '../utils/copyRequest';
import { evaluate } from 'mathjs';

import {
  fetchMurCategories,
  fetchNewMaterialCategories,
  fetchRegions,
  API_BASE_URL,
} from '../services/api';
import { auth } from '../firebaseConfig';
import { AuthContext } from '../context/AuthContext';
import SmartMaterialSearch from '../components/SmartMaterialSearch';
import VisibilityToggle from '../components/VisibilityToggle';
import StyledDropdown from './StyledDropdown';
import CategoryPickerModal from './CategoryPickerModal';
import QuantityCalculatorModal from './QuantityCalculatorModal';
import './EditCompositeModal.css';
import { useCompanyRole } from '../hooks/useCompanyRole';


// ─── constants (mirror BuildNewCompositePage) ──────────────────────────────
const TYPE_BASE = { CO2: 'Co2', BIO: 'Co2-Biogenic', WEIGHT: 'Weight', RATE: 'Rate' };


const getName = (c) =>
  c?.MeasurementUnitName || c?.CostMeasurementName || c?.Name || c?.Description || '';

// ─── helpers ───────────────────────────────────────────────────────────────
const blankMetric = () => ({ measurementId:'', code:'', unit:'', value:0, total:0 });
const blankCarbon = blankMetric;
const blankBio    = blankMetric;
const blankWeight = blankMetric;

// ─── reconstruct env rows from stored LineItemsData ───────────────────────
//
// Submission packs up to 3 items per description (Co2 / Co2-Biogenic / Weight).
// We group by description to get one UI row per material, preserving heading order.
const reconstructEnvRows = (lineItemsData) => {
  const envItems = (lineItemsData || []).filter(
    (li) => !typeMatchesBase(li.measurementId, TYPE_BASE.RATE),
  );

  const seen  = new Map(); // `${heading}::${subHeading}::${description}` → row data
  const order = [];

  for (const li of envItems) {
    const desc       = li.description  || '';
    const heading    = li.heading      || null;
    const subHeading = li.sub_heading  || li.subHeading || null;  // 🆕
    if (!desc) continue;
    const key = `${heading || ''}::${subHeading || ''}::${desc}`;
    if (!seen.has(key)) {
      // assembly_source: a block copied in by "+ Add Assembly" — carried through so a resubmit keeps it
      seen.set(key, { quantity: li.quantity ?? 1, heading, subHeading, desc, assemblyBlock: li.assembly_source || null });
      order.push(key);
    }
  }

  const rows      = [];
  let curHead     = null;
  let curSubHead  = null;   // 🆕

  for (const key of order) {
    const { quantity, heading, subHeading, desc, assemblyBlock } = seen.get(key);
    const blockExtra = assemblyBlock ? { assemblyBlock } : {};

    // ── Heading ─────────────────────────────────────────────────────────
    if (heading && heading !== curHead) {
      curHead    = heading;
      curSubHead = null;    // 🆕 reset sub-heading on new heading
      rows.push({
        id: uuidv4(), isHeading: true, isSubHeading: false,
        heading: curHead, subHeading: null,
        category: '', description: '', quantity: 0, unit: '',
        carbon: blankCarbon(), bio: blankBio(), weight: blankWeight(), locked: false,
        ...(assemblyBlock ? { fromAssembly: assemblyBlock, assemblyBlock } : {}),
      });
    }

    // ── Sub-heading 🆕 ───────────────────────────────────────────────────
    if (subHeading && subHeading !== curSubHead) {
      curSubHead = subHeading;
      rows.push({
        id: uuidv4(), isHeading: false, isSubHeading: true,
        heading: curHead, subHeading: curSubHead,
        category: '', description: '', quantity: 0, unit: '',
        carbon: blankCarbon(), bio: blankBio(), weight: blankWeight(), locked: false,
        ...blockExtra,
      });
    }

    rows.push({
      id: uuidv4(), isHeading: false, isSubHeading: false,
      // ✅ Stable link key (these rows carry no DB id): a normalised heading::subHeading::desc
      // tuple — identical to `seen`'s key, so it's unique per UI row and survives reloads.
      linkKey: normKey(`${heading || ''}::${subHeading || ''}::${desc}`),
      heading:    heading,
      subHeading: subHeading,   // 🆕
      description: desc,
      quantity,
      category: '', unit: '',
      carbon: blankCarbon(), bio: blankBio(), weight: blankWeight(), locked: false,
      ...blockExtra,
    });
  }

  return rows;
};



// ══════════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════════
function EditCompositeModal({ item, onClose, onResubmitted, isUpdateMode = false, updateTargetId = null }) {
  const { user } = useContext(AuthContext);
  const role     = (user?.role || 'user').toLowerCase();
  const { canUpload } = useCompanyRole();   // company admin/reviewer, or anyone not in a company
  // A company's plain users may NOT resubmit (their reviewers do); solo / Nezo users may.
  const resubmitLocked = !canUpload;

  // What is this edit, really?
  //  - isUpdateMode: editing an APPROVED material backed by a real QU → "update"
  //  - otherwise: a never-approved Draft/Pending/ForReview/Rejected working copy
  //    that we're re-sending → "Load to Nezo", NOT "Resubmit"
  const isReSend       = !isUpdateMode;
  const primaryLabel   = isUpdateMode ? 'Resubmit Update' : 'Load to Nezo';

  // ── region / lookups ──────────────────────────────────────────────────
  const regionId = item?.RegionId || 'vquest';

  const [regions,            setRegions]            = useState([]);
  const [carbonCategories,   setCarbonCategories]   = useState([]);
  const [biogenicCategories, setBiogenicCategories] = useState([]);
  const [weightCategories,   setWeightCategories]   = useState([]);
  const [costCategories,     setCostCategories]     = useState([]);
  const [loadingLookups,     setLoadingLookups]     = useState(true);

  // ── form state ────────────────────────────────────────────────────────
  const [materialName,   setMaterialName]   = useState(
    item?.MaterialData?.Name || item?.MaterialData?.BaseMaterialName || '',
  );
  const [materialDesc,   setMaterialDesc]   = useState(
    item?.MaterialData?.Description || '',
  );
  // Material-level Category & Unit (pulled through from the original material,
  // same sources the read-only detail view uses in MyMaterialsPage).
  const [materialCategoryId, setMaterialCategoryId] = useState(
    item?.MaterialData?.CategoryId || item?.MaterialData?.Category || '',
  );
  const [materialUnit, setMaterialUnit] = useState(
    item?.MaterialData?.QuantityTypeName || item?.MaterialData?.QuantityType || '',
  );
  const [materialCategories, setMaterialCategories] = useState([]); // [{id, name}]
  const [userComments,   setUserComments]   = useState('');
  const [visibility,     setVisibility]     = useState(item?.Visibility || 'private');
  const [rows,           setRows]           = useState([]);
  const [rowHistory,     setRowHistory]     = useState({});   // id → calculator rows[]
  const [submitting,     setSubmitting]     = useState(false);
  const [submitError,    setSubmitError]    = useState('');
  const [showSuccess,    setShowSuccess]    = useState(false);
  const [newRequestId,   setNewRequestId]   = useState('');

  // ── quantity calculator modal ─────────────────────────────────────────
  const [modalOpen,    setModalOpen]    = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(null);

  // ── derived region info ───────────────────────────────────────────────
  const regionName = useMemo(
    () => regions.find((r) => r.id === regionId)?.name || item?.RegionName || regionId,
    [regions, regionId, item],
  );

  // regionSuffix kept for display only (regionName chip in the header)
  const regionSuffix = useMemo(() => {
    const r = regions.find((x) => x.id === regionId);
    if (!r || r.country_code === 'NZ') return '';
    return `-${r.country_code}`;
  }, [regions, regionId]);

  // ── load lookup tables — single sequential pass ───────────────────────
  //
  // We must resolve the region suffix BEFORE fetching categories, otherwise
  // rows get seeded against NZ (default) data and show 0 for non-NZ regions.
  //
  // Flow:
  //   1. Fetch regions  → determine suffix for this item's regionId
  //   2. Fetch categories with the correct suffix in one Promise.all
  //   3. Set loadingLookups = false  →  row-seeding effect now runs with
  //      the correct lookup data already in state
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingLookups(true);
      try {
        // Step 1 — regions (needed to compute the suffix)
        const regs = await fetchRegions();
        if (cancelled) return;
        setRegions(regs || []);

        // Step 2 — compute suffix for THIS item's region
        const region = (regs || []).find((x) => x.id === regionId);
        const suffix = (!region || region.country_code === 'NZ')
          ? ''
          : `-${region.country_code}`;

        console.log(`[EditCompositeModal] loading categories with suffix "${suffix}" for region ${regionId}`);

        // Step 3 — fetch the right categories in one shot
        const [carbon, bio, weight, rate] = await Promise.all([
          fetchMurCategories(`vquest-Co2${suffix}`),
          fetchMurCategories(`vquest-Co2-Biogenic${suffix}`),
          fetchMurCategories(`vquest-Weight${suffix}`),
          fetchMurCategories(`vquest-Rate${suffix}`),
        ]);
        if (cancelled) return;

        setCarbonCategories(carbon.data   || []);
        setBiogenicCategories(bio.data    || []);
        setWeightCategories(weight.data   || []);
        setCostCategories(rate.data       || []);
      } catch (e) {
        console.error('[EditCompositeModal] lookup load failed:', e);
      } finally {
        // loadingLookups → false signals the row-seeding effect to run.
        // At this point categories are correct for the item's region.
        if (!cancelled) setLoadingLookups(false);
      }
    })();
    return () => { cancelled = true; };
  }, [regionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── load selectable material categories (for the Category dropdown) ──────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await fetchNewMaterialCategories();
        if (!cancelled) setMaterialCategories(Array.isArray(cats) ? cats : []);
      } catch (e) {
        console.error('[EditCompositeModal] material category load failed:', e);
        if (!cancelled) setMaterialCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── ENV union (for SmartMaterialSearch) ──────────────────────────────
  const ENV_ALL = useMemo(
    () => [...carbonCategories, ...biogenicCategories, ...weightCategories],
    [carbonCategories, biogenicCategories, weightCategories],
  );

  // ── material-level Category / Unit options ───────────────────────────────
  // Hidden categories are filtered out of the picker — same as BuildNewCompositePage.
  const visibleCompositeCategories = useMemo(
    () => materialCategories.filter((c) => !c.IsHidden),
    [materialCategories],
  );

  // Unit options — identical to BuildNewCompositePage's "Create New Material" unit dropdown.
  const unitOptions = useMemo(() => {
    const base = [
      { value: 'SquareMeters', label: 'm²' },
      { value: 'CubicMeters',  label: 'm³' },
      { value: 'LinearMeters', label: 'Linear m' },
      { value: 'Count',        label: 'No.' },
    ];
    // Keep the material's current unit selectable even if it's a legacy value.
    if (materialUnit && !base.some((o) => o.value === materialUnit)) {
      base.unshift({ value: materialUnit, label: materialUnit });
    }
    return base;
  }, [materialUnit]);

  // ── fill / recalc helpers ─────────────────────────────────────────────
  const findCat = useCallback((collection, name) =>
    collection.find((c) => getName(c) === name),
  []);

  const fillForMetric = useCallback((collection, name, quantity) => {
    const c = findCat(collection, name);
    if (!c) return { measurementId:'', code:'', unit:'', value:0, total:0 };
    const value = Number(c.Value || 0);
    const q     = Number(quantity || 0);
    return {
      measurementId: c.MeasurementId        || '',
      code:          c.MeasurementUnitRateId || '',
      unit:          c.QuantityTypeName      || '',
      value,
      total: value * q,
    };
  }, [findCat]);

  const findCategoryForDesc = useCallback((desc) => {
    const hit = ENV_ALL.find((x) => getName(x) === desc)
             || costCategories.find((x) => getName(x) === desc);
    return hit?.Category || '';
  }, [ENV_ALL, costCategories]);

  const recalcEnvRow = useCallback((row) => {
    const desc   = row.description || '';
    const q      = row.quantity    || 0;
    const carbon = fillForMetric(carbonCategories,   desc, q);
    const bio    = fillForMetric(biogenicCategories, desc, q);
    const weight = fillForMetric(weightCategories,   desc, q);
    const unit   = carbon.unit || bio.unit || weight.unit || row.unit || '';
    return { ...row, unit, carbon, bio, weight };
  }, [carbonCategories, biogenicCategories, weightCategories, fillForMetric]);

  // ── initialise rows once lookups are ready ────────────────────────────
  const [rowsSeeded, setRowsSeeded] = useState(false);

  useEffect(() => {
    if (loadingLookups || rowsSeeded || !item?.LineItemsData) return;

    const lineItems = Array.isArray(item.LineItemsData)
      ? item.LineItemsData
      : [];

    const reconstructed = reconstructEnvRows(lineItems);
    const withCategories = reconstructed.map((r) =>
      r.isHeading
        ? r
        : recalcEnvRow({ ...r, category: findCategoryForDesc(r.description) }),
    );

    // ── Restore calculator rows (rowHistory) from stored line_item_rows ──
    // Each UI row maps to up to 3 LineItemsData entries (CO2 / Bio / Weight).
    // We grab line_item_rows from the first entry for that description that
    // has a non-empty calculator payload.
    const newHistory = {};
    for (const uiRow of withCategories) {
      if (uiRow.isHeading) continue;
      const match = lineItems.find(
        (li) =>
          li.description === uiRow.description &&
          Array.isArray(li.line_item_rows) &&
          li.line_item_rows.length > 0,
      );
      if (match) {
        newHistory[uiRow.id] = match.line_item_rows;
      }
    }

    setRows(withCategories);
    setRowHistory(newHistory);
    setRowsSeeded(true);
  }, [loadingLookups, rowsSeeded, item, recalcEnvRow, findCategoryForDesc]);

  // ── row CRUD ──────────────────────────────────────────────────────────
  const updateEnvRow = (id, patch) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? recalcEnvRow({ ...r, ...patch }) : r)),
    );

  const deleteEnvRow = (id) =>
    setRows((prev) => prev.filter((r) => r.id !== id));

  const addEnvRow = () =>
    setRows((prev) => [
      ...prev,
      recalcEnvRow({
        id: uuidv4(), isHeading: false, category: '', description: '',
        quantity: 0, unit: '', carbon: blankCarbon(), bio: blankBio(),
        weight: blankWeight(), locked: false, heading: null,
      }),
    ]);

  const addEnvHeading = () =>
    setRows((prev) => [
      ...prev,
      {
        id: uuidv4(), isHeading: true, heading: 'New Heading',
        category: '', description: '', quantity: 0, unit: '',
        carbon: blankCarbon(), bio: blankBio(), weight: blankWeight(), locked: false,
      },
    ]);

  const addEnvSubHeading = () =>
    setRows((prev) => [
      ...prev,
      {
        id: uuidv4(), isHeading: false, isSubHeading: true,
        heading: null, subHeading: 'New Sub-Heading',
        category: '', description: '', quantity: 0, unit: '',
        carbon: blankCarbon(), bio: blankBio(), weight: blankWeight(), locked: false,
      },
    ]);

  const onQtyChange = (id, raw) => {
    let q = raw;
    try {
      if (String(raw).startsWith('=')) q = evaluate(String(raw).slice(1));
    } catch { /* keep raw */ }
    updateEnvRow(id, { quantity: Number(q) || 0 });
  };

  const handleHeadingChange = (id, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, heading: value } : r)));

  const handleSubHeadingChange = (id, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, subHeading: value } : r)));

  // ── drag & drop ───────────────────────────────────────────────────────
  const handleDragStart = (e, idx) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
    e.currentTarget.classList.add('ecm-row--dragging');
  };

  const handleDragEnd   = (e) => e.currentTarget.classList.remove('ecm-row--dragging');
  const handleDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault(); e.stopPropagation();
    const dragIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (dragIdx === dropIdx) return;

    setRows((prev) => {
      const updated = [...prev];
      const [item]  = updated.splice(dragIdx, 1);
      updated.splice(dropIdx, 0, item);

      // recompute heading association
      let curHead    = null;
      let curSubHead = null;   // 🆕
      return updated.map((r) => {
        if (r.isHeading)    { curHead = r.heading; curSubHead = null; return r; }
        if (r.isSubHeading) { curSubHead = r.subHeading; return r; }    // 🆕
        return { ...r, heading: curHead, subHeading: curSubHead };      // 🆕
      });
    });
  };

  // ── totals ────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    return rows.filter((r) => !r.isHeading && !r.isSubHeading).reduce(
      (acc, r) => ({
        carbon:   acc.carbon   + (r.carbon?.total   || 0),
        biogenic: acc.biogenic + (r.bio?.total       || 0),
        weight:   acc.weight   + (r.weight?.total    || 0),
        uCarbon:  acc.uCarbon  || r.carbon?.unit,
        uBio:     acc.uBio     || r.bio?.unit,
        uWeight:  acc.uWeight  || r.weight?.unit,
      }),
      { carbon:0, biogenic:0, weight:0, uCarbon:'', uBio:'', uWeight:'' },
    );
  }, [rows]);

  // ── save as draft ─────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (submitting) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      let currentHeading    = null;
      let currentSubHeading = null;   // 🆕
      const lineItems = rows.flatMap((r) => {
        if (r.isHeading)    { currentHeading = r.heading; currentSubHeading = null; return []; }
        if (r.isSubHeading) { currentSubHeading = r.subHeading; return []; }  // 🆕

        const packs = [
          { baseId: TYPE_BASE.CO2,    pack: r.carbon },
          { baseId: TYPE_BASE.BIO,    pack: r.bio    },
          { baseId: TYPE_BASE.WEIGHT, pack: r.weight },
        ];
        return packs
          .filter((x) => x.pack && (x.pack.code || x.pack.measurementId))
          .map((x) => ({
            id: uuidv4(), measurementId: x.pack.measurementId || `vquest-${x.baseId}`,
            description: r.description, code: x.pack.code || '',
            quantity: r.quantity, unit: x.pack.unit || '',
            value: x.pack.value || 0, total: x.pack.total || 0,
            line_item_rows: rowHistory[r.id] || [],
            isHeading: false, isSubHeading: false,
            heading:    currentHeading,
            sub_heading: currentSubHeading,   // 🆕
            user_added: true,
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          }));
      });
      const orig = item?.MaterialData || {};
      const material = {
        Id: uuidv4(), BaseMaterialId: orig.Id || orig.BaseMaterialId || null,
        Name: materialName.trim() || orig.Name || 'Draft',
        Description: materialDesc.trim() || `Draft (${regionName})`,
        ColorHex: orig.ColorHex || '#000000',
        QuantityType: materialUnit || orig.QuantityType || '',
        CategoryId: materialCategoryId || orig.CategoryId || null,
      };
      const requestData = {
        material, lineItems, visibility, status: 'Draft', replaceDraftId: newRequestId || item?.RequestId || null, isUpdate: isUpdateMode,
        updateTargetId: updateTargetId || null,
        EnvironmentalData: {
          carbon:   { value: totals.carbon,   unit: totals.uCarbon  },
          biogenic: { value: totals.biogenic, unit: totals.uBio     },
          weight:   { value: totals.weight,   unit: totals.uWeight  },
        },
        userComments, regionId, regionName,
        submittedBy: { uid: auth.currentUser?.uid || 'unknown', email: auth.currentUser?.email || 'unknown', role },
        submittedAt: new Date().toISOString(),
      };
      const resp = await fetch(`${API_BASE_URL}/composite-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`,
          'x-region': regionId, 'X-User-ID': auth.currentUser?.uid || 'unknown',
          'X-User-Email': auth.currentUser?.email || 'unknown', 'X-User-Role': role },
        body: JSON.stringify(requestData),
      });
      if (!resp.ok) throw new Error(await resp.text() || 'Failed to save draft');
      setNewRequestId((await resp.json()).requestId);
      setShowSuccess(true);
    } catch (e) {
      setSubmitError(e.message || 'Failed to save draft.');
    } finally { setSubmitting(false); }
  };

  const handleSendForReview = async () => {
    if (submitting) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      let currentHeading    = null;
      let currentSubHeading = null;
      const lineItems = rows.flatMap((r) => {
        if (r.isHeading)    { currentHeading = r.heading; currentSubHeading = null; return []; }
        if (r.isSubHeading) { currentSubHeading = r.subHeading; return []; }
        const packs = [
          { baseId: TYPE_BASE.CO2,    pack: r.carbon },
          { baseId: TYPE_BASE.BIO,    pack: r.bio    },
          { baseId: TYPE_BASE.WEIGHT, pack: r.weight },
        ];
        return packs
          .filter((x) => x.pack && (x.pack.code || x.pack.measurementId))
          .map((x) => ({
            id: uuidv4(), measurementId: x.pack.measurementId || `vquest-${x.baseId}`,
            description: r.description, code: x.pack.code || '',
            quantity: r.quantity, unit: x.pack.unit || '',
            value: x.pack.value || 0, total: x.pack.total || 0,
            line_item_rows: rowHistory[r.id] || [],
            isHeading: false, isSubHeading: false,
            heading: currentHeading, sub_heading: currentSubHeading,
            user_added: true,
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          }));
      });
      const orig = item?.MaterialData || {};
      const material = {
        Id: uuidv4(), BaseMaterialId: orig.Id || orig.BaseMaterialId || null,
        Name: materialName.trim() || orig.Name || 'For Review',
        Description: materialDesc.trim() || `For Review (${regionName})`,
        ColorHex: orig.ColorHex || '#000000',
        QuantityType: materialUnit || orig.QuantityType || '',
        CategoryId: materialCategoryId || orig.CategoryId || null,
      };
      const requestData = {
        material, lineItems, visibility,
        status: 'ForReview',
        replaceDraftId: newRequestId || item?.RequestId || null,
        isUpdate: isUpdateMode,
        updateTargetId: updateTargetId || null,
        EnvironmentalData: {
          carbon:   { value: totals.carbon,   unit: totals.uCarbon  },
          biogenic: { value: totals.biogenic, unit: totals.uBio     },
          weight:   { value: totals.weight,   unit: totals.uWeight  },
        },
        userComments, regionId, regionName,
        submittedBy: { uid: auth.currentUser?.uid || 'unknown', email: auth.currentUser?.email || 'unknown', role },
        submittedAt: new Date().toISOString(),
      };
      const resp = await fetch(`${API_BASE_URL}/composite-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', Authorization: `Bearer ${token}`,
          'x-region': regionId, 'X-User-ID': auth.currentUser?.uid || 'unknown',
          'X-User-Email': auth.currentUser?.email || 'unknown', 'X-User-Role': role,
        },
        body: JSON.stringify(requestData),
      });
      if (!resp.ok) throw new Error(await resp.text() || 'Failed to send for review');
      setNewRequestId((await resp.json()).requestId);
      setShowSuccess(true);
    } catch (e) {
      setSubmitError(e.message || 'Failed to send for review.');
    } finally { setSubmitting(false); }
  };

  // ── submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting) return;
    const envRows = rows.filter((r) => !r.isHeading);
    if (!envRows.length) {
      setSubmitError('Add at least one line item before submitting.');
      return;
    }

    setSubmitError('');
    setSubmitting(true);

    try {
      const token = await auth.currentUser.getIdToken();

      // Build line items (same shape as BuildNewCompositePage)
      let currentHeading    = null;
      let currentSubHeading = null;   // 🆕
      const lineItems = rows.flatMap((r) => {
        if (r.isHeading)    { currentHeading = r.heading; currentSubHeading = null; return []; }
        if (r.isSubHeading) { currentSubHeading = r.subHeading; return []; }  // 🆕

        const packs = [
          { baseId: TYPE_BASE.CO2,    pack: r.carbon },
          { baseId: TYPE_BASE.BIO,    pack: r.bio    },
          { baseId: TYPE_BASE.WEIGHT, pack: r.weight },
        ];
        return packs
          .filter((x) => x.pack && (x.pack.code || x.pack.measurementId))
          .map((x) => ({
            id:            uuidv4(),
            measurementId: x.pack.measurementId || `vquest-${x.baseId}`,
            description:   r.description,
            code:          x.pack.code  || '',
            quantity:      r.quantity,
            unit:          x.pack.unit  || '',
            value:         x.pack.value || 0,
            total:         x.pack.total || 0,
            line_item_rows: rowHistory[r.id] || [],
            isHeading:    false,
            isSubHeading: false,
            heading:      currentHeading,
            sub_heading:  currentSubHeading,   // 🆕
            user_added:   true,
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          }));
      });

      const origMaterial = item?.MaterialData || {};
      const material = {
        Id:           uuidv4(),
        BaseMaterialId: origMaterial.Id || origMaterial.BaseMaterialId || null,
        Name:         materialName.trim() || origMaterial.Name || 'Edited Composite',
        Description:  materialDesc.trim()
          || `Edited request (${regionName})`,
        ColorHex:     origMaterial.ColorHex  || '#000000',
        QuantityType: materialUnit || origMaterial.QuantityType || '',
        CategoryId:   materialCategoryId || origMaterial.CategoryId || null,
      };

      const requestData = {
        material,
        lineItems,
        visibility,
        replaceDraftId: newRequestId || item?.RequestId || null,
        isUpdate: isUpdateMode,
        updateTargetId: updateTargetId || null,
        EnvironmentalData: {
          carbon:   { value: totals.carbon,   unit: totals.uCarbon  },
          biogenic: { value: totals.biogenic, unit: totals.uBio     },
          weight:   { value: totals.weight,   unit: totals.uWeight  },
        },
        userComments,
        regionId:    regionId,
        regionName:  regionName,
        submittedBy: {
          uid:   auth.currentUser?.uid   || 'unknown',
          email: auth.currentUser?.email || 'unknown',
          role,
        },
        submittedAt: new Date().toISOString(),
      };

      const resp = await fetch(`${API_BASE_URL}/composite-requests`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
          'x-region':      regionId,
          'X-User-ID':     auth.currentUser?.uid   || 'unknown',
          'X-User-Email':  auth.currentUser?.email || 'unknown',
          'X-User-Role':   role,
        },
        body: JSON.stringify(requestData),
      });

      if (!resp.ok) throw new Error(await resp.text() || 'Submission failed');

      const result = await resp.json();
      setNewRequestId(result.requestId);
      setShowSuccess(true);

    } catch (e) {
      console.error('[EditCompositeModal] submit failed:', e);
      setSubmitError(e.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const GRID_ENV = '36px minmax(380px,2.8fr) 110px 120px 140px 140px 140px 80px';

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <>
    <div className="ecm-overlay" onClick={onClose}>
      <div className="ecm-panel" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="ecm-header">
          <div className="ecm-header__left">
            <span className="ecm-header__icon">✏️</span>
            <div>
              <h2 className="ecm-header__title">Edit &amp; Resubmit</h2>
              <p className="ecm-header__subtitle">
                {isUpdateMode
                  ? `Updating approved material · ${regionName}`
                  : `${regionName} · Editing will replace the existing request`}
              </p>
            </div>
          </div>
          <button className="ecm-header__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* ── Scroll body ── */}
        <div className="ecm-body">

          {loadingLookups ? (
            <div className="ecm-loading">
              <div className="ecm-loading__spinner" />
              <p>Loading lookup data…</p>
            </div>
          ) : (
            <>
              {/* ── Material name / description ── */}
              <div className="ecm-section">
                <h3 className="ecm-section__title">Material Details</h3>
                <div className="ecm-meta-grid">
                  <div className="ecm-meta-field">
                    <label className="ecm-label">Material Name</label>
                    <input
                      className="ecm-input"
                      type="text"
                      value={materialName}
                      onChange={(e) => setMaterialName(e.target.value)}
                      placeholder="Enter material name…"
                    />
                  </div>
                  <div className="ecm-meta-field">
                    <label className="ecm-label">Description</label>
                    <textarea
                      className="ecm-input ecm-input--textarea"
                      rows={2}
                      value={materialDesc}
                      onChange={(e) => setMaterialDesc(e.target.value)}
                      placeholder="Optional description…"
                    />
                  </div>
                  <div className="ecm-meta-field">
                    <label className="ecm-label">Category</label>
                    <CategoryPickerModal
                      categories={visibleCompositeCategories}
                      value={materialCategoryId}
                      onChange={setMaterialCategoryId}
                      placeholder="Search and select a category…"
                      title="Select Category"
                    />
                  </div>
                  <div className="ecm-meta-field">
                    <label className="ecm-label">Unit</label>
                    {/* spacer offsets StyledDropdown's internal marginTop:-15 */}
                    <div style={{ marginTop: 15 }}>
                      <StyledDropdown
                        name="ecm-material-unit"
                        options={unitOptions}
                        value={materialUnit}
                        onChange={setMaterialUnit}
                        placeholder="Select unit…"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── ENV line items ── */}
              <div className="ecm-section">
                <h3 className="ecm-section__title">
                  Environmental Line Items
                  <span className="ecm-section__count">
                    {rows.filter((r) => !r.isHeading).length} items
                  </span>
                </h3>

                {/* column headers */}
                <div
                  className="ecm-col-header"
                  style={{ display:'grid', gridTemplateColumns: GRID_ENV, gap:10 }}
                >
                  <span />
                  <span>Material</span>
                  <span>Quantity</span>
                  <span>Unit</span>
                  <span>Carbon Total</span>
                  <span>Biogenic Total</span>
                  <span>Weight Total</span>
                  <span>Del</span>
                </div>

                {rows.map((r, idx) => {

                  // ── Heading row ─────────────────────────────────────────
                  if (r.isHeading) return (
                    <div
                      key={r.id}
                      className="ecm-row ecm-row--heading"
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                      style={{ display:'grid', gridTemplateColumns:'36px 1fr auto', gap:10, alignItems:'center' }}
                    >
                      <span className="ecm-drag-handle">⋮⋮</span>
                      <input
                        className="ecm-input ecm-input--heading"
                        type="text"
                        value={r.heading || ''}
                        placeholder="Section heading…"
                        onChange={(e) => handleHeadingChange(r.id, e.target.value)}
                      />
                      <button
                        className="ecm-btn ecm-btn--danger ecm-btn--sm"
                        onClick={() => deleteEnvRow(r.id)}
                      >
                        Delete
                      </button>
                    </div>
                  );

                  // ── Sub-heading row 🆕 ──────────────────────────────────
                  if (r.isSubHeading) return (
                    <div
                      key={r.id}
                      className="ecm-row ecm-row--subheading"
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '36px 20px 1fr auto',
                        gap: 10, alignItems: 'center',
                        padding: '6px 0 6px 24px',
                        background: '#f7f9fc',
                        borderLeft: '3px solid #a0bfdf',
                      }}
                    >
                      <span className="ecm-drag-handle">⋮⋮</span>
                      <span style={{ color: '#7a9cbf', fontSize: 15 }}>└</span>
                      <input
                        className="ecm-input"
                        type="text"
                        value={r.subHeading || ''}
                        placeholder="Sub-heading…"
                        onChange={(e) => handleSubHeadingChange(r.id, e.target.value)}
                        style={{ fontWeight: 600, fontSize: 13, color: '#2c5282' }}
                      />
                      <button
                        className="ecm-btn ecm-btn--danger ecm-btn--sm"
                        onClick={() => deleteEnvRow(r.id)}
                      >
                        Delete
                      </button>
                    </div>
                  );

                  // ── Data row ────────────────────────────────────────────
                  return (
                    <div
                      key={r.id}
                      className="ecm-row"
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                      style={{ display:'grid', gridTemplateColumns: GRID_ENV, gap:10, alignItems:'center' }}
                    >
                      <span className="ecm-drag-handle">⋮⋮</span>

                      <div>
                        <SmartMaterialSearch
                          name={`ecm-search-${r.id}`}
                          materials={ENV_ALL}
                          value={r.description}
                          category={r.category}
                          onChange={(desc, cat) =>
                            updateEnvRow(r.id, { description: desc, category: cat })
                          }
                          placeholder="Search material…"
                        />
                      </div>

                      <input
                        className="ecm-input ecm-input--num ecm-input--qty-clickable"
                        type="text"
                        value={r.quantity}
                        readOnly
                        title="Click to open quantity calculator"
                        onFocus={(e) => { e.stopPropagation(); setActiveRowIdx(idx); setModalOpen(true); }}
                        onClick={(e)  => { e.stopPropagation(); setActiveRowIdx(idx); setModalOpen(true); }}
                      />

                      <input className="ecm-input ecm-input--ro" type="text"   readOnly value={r.unit} />
                      <input className="ecm-input ecm-input--ro" type="number" readOnly value={(r.carbon?.total  ?? 0).toFixed(4)} />
                      <input className="ecm-input ecm-input--ro" type="number" readOnly value={(r.bio?.total     ?? 0).toFixed(4)} />
                      <input className="ecm-input ecm-input--ro" type="number" readOnly value={(r.weight?.total  ?? 0).toFixed(4)} />

                      <button
                        className="ecm-btn ecm-btn--danger ecm-btn--sm"
                        onClick={() => deleteEnvRow(r.id)}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}

                <div className="ecm-row-actions">
                  <button className="ecm-btn ecm-btn--add" onClick={addEnvRow}>
                    + Add Line Item
                  </button>
                  <button className="ecm-btn ecm-btn--heading" onClick={addEnvHeading}>
                    + Add Heading
                  </button>
                  <button
                    className="ecm-btn ecm-btn--heading"
                    style={{ background: '#e0f2f1', color: '#00695c', borderColor: '#b2dfdb' }}
                    onClick={addEnvSubHeading}
                  >
                    + Add Sub-Heading
                  </button>
                </div>

                {/* Totals chips */}
                <div className="ecm-totals">
                  <span className="ecm-chip">🌡️ Carbon: <b>{totals.carbon.toFixed(3)}</b> {totals.uCarbon}</span>
                  <span className="ecm-chip">🌱 Biogenic: <b>{totals.biogenic.toFixed(3)}</b> {totals.uBio}</span>
                  <span className="ecm-chip">⚖️ Weight: <b>{totals.weight.toFixed(3)}</b> {totals.uWeight}</span>
                </div>
              </div>

              {/* ── Comments ── */}
              <div className="ecm-section">
                <h3 className="ecm-section__title">Comments for Reviewer</h3>
                <textarea
                  className="ecm-input ecm-input--textarea"
                  rows={3}
                  placeholder="Describe what you changed or any assumptions the reviewer should know…"
                  value={userComments}
                  onChange={(e) => setUserComments(e.target.value)}
                />
              </div>

              {/* ── Visibility ── */}
              <div className="ecm-section">
                <VisibilityToggle value={visibility} onChange={setVisibility} />
              </div>

              {/* ── Error ── */}
              {submitError && (
                <div className="ecm-alert ecm-alert--error">⚠️ {submitError}</div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="ecm-footer">
          <button className="ecm-btn ecm-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="ecm-btn ecm-btn--draft"
            onClick={handleSaveDraft}
            disabled={submitting || loadingLookups}
          >
            {submitting ? 'Saving…' : '💾 Save as Draft'}
          </button>
          {/* 🆕 */}
          <button
            className="ecm-btn ecm-btn--review"
            onClick={handleSendForReview}
            disabled={submitting || loadingLookups}
          >
            {submitting ? 'Saving…' : '🔍 Send for Review'}
          </button>
          <button
            className="ecm-btn ecm-btn--submit"
            onClick={handleSubmit}
            disabled={
              resubmitLocked ||
              submitting || loadingLookups ||
              rows.filter((r) => !r.isHeading && !r.isSubHeading).length === 0
            }
            title={resubmitLocked
              ? 'Only reviewers or admins can send this to Nezo. You can Save as Draft or Send for Review.'
              : undefined}
          >
            {submitting ? 'Submitting…' : primaryLabel}
            {resubmitLocked && ' 🔒'}
          </button>
        </div>
      </div>

      {/* ── Success overlay ── */}
      {showSuccess && (
        <div className="ecm-success-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="ecm-success-card">
            <div className="ecm-success-card__emoji">🎉</div>
            <h2>Done!</h2>
            <p>Your composite request has been saved. Check My Materials to continue.</p>
            <div className="ecm-success-card__meta">
              <div><b>New Request ID:</b> {newRequestId}</div>
              <div><b>Region:</b> {regionName}</div>
              <div><b>Visibility:</b> {visibility === 'private' ? '🔒 Private' : '🌐 Public'}</div>
            </div>
            <button
              className="ecm-btn ecm-btn--submit"
              onClick={() => {
                setShowSuccess(false);
                onResubmitted?.();
                onClose();
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>

    {/* ── Quantity calculator modal — rendered OUTSIDE the overlay so its
         clicks cannot bubble up to ecm-overlay's onClose handler ── */}
    <QuantityCalculatorModal
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      onSave={(qty, calcRows) => {
        const row = rows[activeRowIdx];
        if (!row || row.isHeading) { setModalOpen(false); return; }

        // ✅ Live links: apply this row's new quantity, then propagate to linked rows.
        const baseRows = rows.map((r) => (r.id === row.id ? recalcEnvRow({ ...r, quantity: qty }) : r));
        const resolvedHistory = { ...rowHistory, [row.id]: calcRows };
        resolveLineItemLinks(baseRows, {
          rowsOf: (item) => resolvedHistory[item.id],
          setRows: (item, rws) => { resolvedHistory[item.id] = rws; },
          setQuantity: (item, q) => {
            const idx = baseRows.findIndex((b) => b.id === item.id);
            if (idx >= 0) baseRows[idx] = recalcEnvRow({ ...baseRows[idx], quantity: q });
          },
        });
        setRows(baseRows);
        setRowHistory(resolvedHistory);
        setModalOpen(false);
      }}
      initialRows={rowHistory[rows[activeRowIdx]?.id] || []}
      lineItems={rows.filter((r) => !r.isHeading && r.description).map((r) => ({
        ...r,
        FactorId: r.id,
        CostMeasurementName: r.description,
        Category: r.category,
        QuantityTypeName: r.unit,
        Factor: r.quantity,
        sectionType: 'Environmental',
      }))}
      sectionType="Environmental"
      // ✅ Cycle-prevention identity must use the same stable linkKey as the links
      // themselves (rowHistory stays keyed by row.id for save/load).
      currentItemId={rows[activeRowIdx]?.linkKey}
      lineItemRowsById={rows.reduce((acc, r) => {
        if (!r.isHeading && r.linkKey != null) acc[String(r.linkKey)] = rowHistory[r.id] || [];
        return acc;
      }, {})}
      // unit context for the mismatch advisor + Conversion helper (this modal's `materialUnit` state
      // is the COMPOSITE's unit; the line item's unit is the row's, else its metric packs')
      compositeUnit={materialUnit}
      materialUnit={rows[activeRowIdx]?.unit || rows[activeRowIdx]?.weight?.unit || rows[activeRowIdx]?.carbon?.unit || ''}
      materialName={rows[activeRowIdx]?.description ?? ''}
      materialWeight={rows[activeRowIdx]?.weight}
    />
    </>
  );
}

export default EditCompositeModal;